import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { App } from './App';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Input } from './pages/Input';
import { MarketKnowledge } from './pages/MarketKnowledge';
import { Capture } from './pages/Capture';
import { Pulse } from './pages/Pulse';
import { Clients } from './pages/Clients';
import { ClientDetail } from './pages/ClientDetail';
import { Insights } from './pages/Insights';
import { DemandOverTime } from './pages/DemandOverTime';
import { StrategyLandscape } from './pages/StrategyLandscape';
import { Roadmap } from './pages/Roadmap';
import { LoopView } from './pages/LoopView';
import { Review } from './pages/Review';
import { MarketMood } from './pages/MarketMood';
import { UserManagement } from './pages/UserManagement';
import { getAuth } from './lib/api';
import './styles.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = getAuth();
  if (!auth) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Fixes the broken literal-string redirect that was at main.tsx:88 */
function RedirectToRoadmapItem() {
  const { roadmapItemId } = useParams<{ roadmapItemId: string }>();
  return <Navigate to={`/roadmap/${roadmapItemId}`} replace />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <App />
            </ProtectedRoute>
          }
        >
          {/* ── MNEMO ── */}
          <Route index element={<Dashboard />} />

          <Route path="roadmap">
            <Route index element={<Roadmap />} />
            <Route path=":roadmapItemId" element={<LoopView />} />
          </Route>

          <Route path="clients" element={<Clients />} />
          <Route path="clients/:clientId" element={<ClientDetail />} />

          <Route path="pulse" element={<Pulse />} />

          {/* ── OPERATIONS ── */}
          <Route path="capture" element={<Capture />}>
            <Route index element={<Navigate to="/capture/transcript" replace />} />
            <Route path="transcript" element={<Input />} />
            <Route path="articles" element={<MarketKnowledge />} />
          </Route>

          {/* /validate → Review (extraction gate) */}
          <Route path="validate" element={<Review />} />

          {/* ── SETTINGS ── */}
          <Route path="settings/market-mood" element={<MarketMood />} />
          <Route path="settings/users" element={<UserManagement />} />

          {/* ── PATTERNS (demoted survivors) ── */}
          <Route path="patterns/insights" element={<Insights />} />
          <Route path="patterns/demand" element={<DemandOverTime />} />
          <Route path="patterns/landscape" element={<StrategyLandscape />} />

          {/* ── LEGACY REDIRECTS ── */}
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          <Route path="patterns/roadmap" element={<Navigate to="/roadmap" replace />} />
          <Route path="patterns/roadmap/:roadmapItemId" element={<RedirectToRoadmapItem />} />
          <Route path="patterns/narrative" element={<Navigate to="/roadmap?tab=narrative" replace />} />
          <Route path="review" element={<Navigate to="/validate" replace />} />
          <Route path="query" element={<Navigate to="/" replace />} />
          <Route path="transcript-query" element={<Navigate to="/" replace />} />
          <Route path="client-mood" element={<Navigate to="/clients" replace />} />
          <Route path="commitments" element={<Navigate to="/clients" replace />} />
          <Route path="standing-items" element={<Navigate to="/clients" replace />} />
          <Route path="strategy-mentions" element={<Navigate to="/clients" replace />} />
          <Route path="attention" element={<Navigate to="/pulse" replace />} />
          <Route path="flywheel" element={<Navigate to="/roadmap?tab=narrative" replace />} />
          <Route path="input" element={<Navigate to="/capture/transcript" replace />} />
          <Route path="market-knowledge" element={<Navigate to="/capture/articles" replace />} />
          <Route path="insights" element={<Navigate to="/patterns/insights" replace />} />
          <Route path="demand-over-time" element={<Navigate to="/patterns/demand" replace />} />
          <Route path="strategy-landscape" element={<Navigate to="/patterns/landscape" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);
